import React from "react";
import Loader from "./Loader";

/**
 * Task 37 V2 — the block that stands in for a panel's contents when there are none.
 *
 * FOUR OUTCOMES SHARE THIS SPACE AND THEY ARE NOT THE SAME MESSAGE: still loading, failed to load,
 * genuinely empty, or nothing prepared yet. Collapsing them is the defect this exists to remove.
 * Every panel in this feature used to report a failed fetch with a toast and then render its empty
 * state, so "we could not reach the server" and "you have no customers" looked identical once the
 * toast had gone — and the Sync tab was worse still, showing a hardcoded "Loading…" for ever.
 *
 * Shaped on BillingSyncSteps/DryRunPanel.js, which already did exactly this for the preview tab
 * after the client asked for an expired preview to say so rather than look like a fresh one. The
 * markup is byte-identical to the eight hand-written copies across Tasks 9, 36 and 37, so adopting
 * it changes no pixels.
 *
 * Presentational only. It decides nothing — the caller says which state it is in.
 *
 * @param busy        A fetch is in flight. Wins over everything, so a retry never shows the error
 *                    it is in the middle of clearing.
 * @param error       The message from handleApiError. Already safe to show: it is the server's own
 *                    wording or the caller's fallback, never a raw exception.
 * @param title       Headline for the ordinary empty case. NEVER shown while busy — see busyTitle.
 * @param busyTitle   Headline while busy. Defaults to "Loading…".
 *
 *                    THESE ARE TWO DIFFERENT HEADLINES AND THEY USED TO BE ONE. `title` was read as
 *                    "the headline", with "Loading…" as its fallback — so a panel that had an empty
 *                    state passed a title, and that title won while the fetch was still running.
 *                    Customers announced "You have no customers yet.", Products "There is nothing to
 *                    map yet." and the status screen "Nothing has been billed yet.", each for work
 *                    that had not finished and might yet return rows.
 *
 *                    That is the very defect this component was written to remove, in the busy
 *                    dimension instead of the error one. Keeping them apart is what stops it coming
 *                    back: a new caller now gets "Loading…" by default rather than by luck.
 * @param detail      Explanation for the ordinary empty case.
 * @param busyDetail  Explanation while busy. Omitted by most callers — a gif and "Loading…" say
 *                    enough — but a long job worth reassuring somebody about can say so. Separate
 *                    from `detail` for the same reason busyTitle is: the empty-state wording is
 *                    wrong for work still in progress.
 * @param actionLabel Button text. The button is omitted entirely when there is no onAction.
 * @param onAction    What the button does — usually the same load function that just failed.
 * @param busyLabel   Button text while busy. Defaults to the action label.
 */
export default function PanelState({
    busy, error, title, busyTitle, detail, busyDetail, actionLabel, onAction, busyLabel,
}) {
    /**
     * A PANEL WITH NOTHING TO REPORT IS A PANEL WHOSE DATA HAS NOT ARRIVED YET.
     *
     * A caller with no title, no error and no busy flag used to render a headline of `undefined` —
     * an empty bordered box. ActivationPanel does exactly that for the frame between mounting and
     * its first beginTabLoad, and for as long as a 440 takes to navigate away, because handleApiError
     * returns null there and no error state is ever set.
     *
     * Reading that as loading is both the honest answer — the fetch has not started, not failed —
     * and the only one that cannot render nothing. Note this is NOT the `title || "Loading…"`
     * fallback removed below: it never prefers the empty-state wording over the loading one, it
     * only refuses to render a panel that says nothing at all.
     */
    const nothingToReport = !busy && !error && !title;
    const showBusy = busy || nothingToReport;

    // NOT `title || "Loading…"`. The empty-state headline must never stand in for the loading one;
    // that fallback is exactly how a fetch in flight came to report an empty result.
    const headline = showBusy
        ? (busyTitle || "Loading…")
        : error
            ? "This could not be loaded."
            : title;

    const explanation = showBusy ? busyDetail : error || detail;

    // A button only where there is something for it to do. The empty case usually has nothing —
    // there is no point offering "Try again" to a reseller who simply has no customers yet.
    const showAction = !!onAction && !!actionLabel;

    return (
        <div className="alert alert-light border text-center py-5 mb-0">
            {/* The product's own indicator, so these screens read as working rather than as a
                message. Here rather than at each call site: six of them share this block. */}
            {showBusy && (
                <div className="mb-2">
                    <Loader />
                </div>
            )}
            <p className="mb-1 fw-semibold">{headline}</p>
            {explanation && (
                <p
                    className={`${showAction ? "mb-3" : "mb-0"} text-muted`}
                    style={{ fontSize: 14 }}
                >
                    {explanation}
                </p>
            )}
            {showAction && (
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={showBusy}
                    onClick={onAction}
                >
                    {showBusy ? (busyLabel || actionLabel) : actionLabel}
                </button>
            )}
        </div>
    );
}
